import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Check, Plus, Trash2, AlertTriangle } from "lucide-react";
import {
  getDiscordLeadChannels,
  updateDiscordLeadChannels,
  type LeadDiscordChannelConfig,
} from "../../services/AppConfigService";

const emptyRow = (): LeadDiscordChannelConfig => ({ channelId: "", retailer: "walmart", category: "" });

export default function LeadCategoriesTab() {
  const qc = useQueryClient();
  const [rows, setRows] = useState<LeadDiscordChannelConfig[]>([]);
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ["discord-lead-channels"],
    queryFn: getDiscordLeadChannels,
  });

  useEffect(() => {
    if (data?.channels?.length !== undefined) {
      setRows(data.channels.length ? data.channels : [emptyRow()]);
      setDirty(false);
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: (ch: LeadDiscordChannelConfig[]) => updateDiscordLeadChannels(ch),
    onSuccess: (res) => {
      qc.setQueryData(["discord-lead-channels"], res);
      setRows(res.channels?.length ? res.channels : [emptyRow()]);
      setDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  const updateRow = useCallback((i: number, patch: Partial<LeadDiscordChannelConfig>) => {
    setRows((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], ...patch };
      return next;
    });
    setDirty(true);
  }, []);

  const removeRow = (i: number) => {
    setRows((prev) => (prev.length <= 1 ? [emptyRow()] : prev.filter((_, j) => j !== i)));
    setDirty(true);
  };

  const addRow = () => {
    setRows((prev) => [...prev, emptyRow()]);
    setDirty(true);
  };

  const save = () =>
    mutation.mutate(rows.map((r) => ({ channelId: r.channelId.trim(), retailer: r.retailer.trim(), category: (r.category || "").trim() })));

  if (isLoading)
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );

  if (error)
    return <p className="text-sm text-red-600 dark:text-red-400">Failed to load discord lead channels</p>;

  return (
    <div className="max-w-xl">
      {data?.configMissing && (
        <div className="mb-4 flex gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>No Dynamo row yet — preview shows bootstrap defaults until you Save.</span>
        </div>
      )}

      <div className="space-y-2">
        {rows.map((r, i) => (
          <div
            key={i}
            className="grid grid-cols-1 sm:grid-cols-12 gap-2 rounded-lg border border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-800 p-3"
          >
            <div className="sm:col-span-5">
              <label className="block text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">Channel ID</label>
              <input
                className="w-full px-2 py-1.5 text-sm rounded-md border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-900 text-gray-900 dark:text-white font-mono"
                value={r.channelId}
                placeholder="snowflake ID"
                onChange={(e) => updateRow(i, { channelId: e.target.value })}
              />
            </div>
            <div className="sm:col-span-3">
              <label className="block text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">Retailer</label>
              <select
                className="w-full px-2 py-1.5 text-sm rounded-md border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-900 text-gray-900 dark:text-white"
                value={r.retailer}
                onChange={(e) => updateRow(i, { retailer: e.target.value })}
              >
                <option value="walmart">walmart</option>
                <option value="home_depot">home_depot</option>
                <option value="target">target</option>
              </select>
            </div>
            <div className="sm:col-span-3">
              <label className="block text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">Category (optional)</label>
              <input
                className="w-full px-2 py-1.5 text-sm rounded-md border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-900 text-gray-900 dark:text-white"
                value={r.category || ""}
                placeholder="e.g. Clothing"
                onChange={(e) => updateRow(i, { category: e.target.value })}
              />
            </div>
            <div className="sm:col-span-1 flex items-end justify-end">
              <button
                type="button"
                onPointerDown={() => removeRow(i)}
                className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                aria-label="Remove row"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onPointerDown={addRow}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-300 dark:border-dark-600 text-gray-700 dark:text-dark-200 hover:bg-gray-50 dark:hover:bg-dark-700"
        >
          <Plus className="w-3.5 h-3.5" /> Add row
        </button>
        <button
          type="button"
          onPointerDown={save}
          disabled={!dirty || mutation.isPending}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-40"
        >
          {mutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : saved ? <Check className="w-3.5 h-3.5" /> : null}
          {saved ? "Saved" : "Save"}
        </button>
        {mutation.isError && <span className="text-xs text-red-600 self-center">Save failed</span>}
      </div>
    </div>
  );
}
